import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import {
  RiDashboardLine,
  RiMapPinLine,
  RiTeamLine,
  RiStoreLine,
  RiLockLine,
  RiCalendarLine,
  RiBuildingLine,
  RiFolderLine,
  RiMoneyDollarCircleLine,
  RiFileTextLine,
  RiContactsLine,
  RiMessageLine,
  RiMailLine,
  RiShieldLine,
  RiErrorWarningLine,
  RiUserLine,
  RiPagesLine,
  RiLayoutGridLine,
  RiStackLine,
  RiEditLine,
  RiTableLine,
  RiBarChartLine,
  RiImageLine,
  RiMapPin2Line,
  RiGitBranchLine,
  RiArrowDownSFill,
  RiLogoutCircleLine
} from 'react-icons/ri'


const Sidebar = () => {
  const navigate = useNavigate()

  const handleLogout = (e) => {
    e.preventDefault()
    Cookies.remove('token')
    navigate('/')
  }

  useEffect(() => {
    // Initialize sidebar dropdown functionality after component mounts
    const initializeSidebar = () => {
      const ANIMATION_DURATION = 300;

      const slideUp = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        const { parentElement } = target;
        if (parentElement) {
          parentElement.classList.remove("open");
        }
        target.style.transitionProperty = "height, margin, padding";
        target.style.transitionDuration = `${duration}ms`;
        target.style.boxSizing = "border-box";
        target.style.height = `${target.offsetHeight}px`;
        void target.offsetHeight; // Force reflow
        target.style.overflow = "hidden";
        target.style.height = 0;
        target.style.paddingTop = 0;
        target.style.paddingBottom = 0;
        target.style.marginTop = 0;
        target.style.marginBottom = 0;
        window.setTimeout(() => {
          if (target && target.style) {
            target.style.display = "none";
            target.style.removeProperty("height");
            target.style.removeProperty("padding-top");
            target.style.removeProperty("padding-bottom");
            target.style.removeProperty("margin-top");
            target.style.removeProperty("margin-bottom");
            target.style.removeProperty("overflow");
            target.style.removeProperty("transition-duration");
            target.style.removeProperty("transition-property");
          }
        }, duration);
      };

      const slideDown = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        const { parentElement } = target;
        if (parentElement) {
          parentElement.classList.add("open");
        }
        target.style.removeProperty("display");
        let { display } = window.getComputedStyle(target);
        if (display === "none") display = "block";
        target.style.display = display;
        const height = target.offsetHeight;
        target.style.overflow = "hidden";
        target.style.height = 0;
        target.style.paddingTop = 0;
        target.style.paddingBottom = 0;
        target.style.marginTop = 0;
        target.style.marginBottom = 0;
        void target.offsetHeight; // Force reflow
        target.style.boxSizing = "border-box";
        target.style.transitionProperty = "height, margin, padding";
        target.style.transitionDuration = `${duration}ms`;
        target.style.height = `${height}px`;
        target.style.removeProperty("padding-top");
        target.style.removeProperty("padding-bottom");
        target.style.removeProperty("margin-top");
        target.style.removeProperty("margin-bottom");
        window.setTimeout(() => {
          if (target && target.style) {
            target.style.removeProperty("height");
            target.style.removeProperty("overflow");
            target.style.removeProperty("transition-property");
            target.style.removeProperty("transition-duration");
          }
        }, duration);
      };

      const slideToggle = (target, duration = ANIMATION_DURATION) => {
        if (!target) return;
        if (window.getComputedStyle(target).display === "none") {
          return slideDown(target, duration);
        }
        return slideUp(target, duration);
      };

      // Store event handlers for cleanup
      const clickHandlers = [];

      // First level menu items
      const firstLevelItems = document.querySelectorAll(".nav > ul > .slide.has-sub > a");
      firstLevelItems.forEach((element) => {
        const clickHandler = (e) => {
          try {
            // Check if element still exists and is connected to DOM
            if (!element || !element.isConnected) return;

            const nextSibling = element.nextElementSibling;

            // Only prevent default and toggle if there's a submenu (UL element)
            if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'UL') {
              e.preventDefault();

              const parentMenu = element.closest(".nav.sub-open");
              if (parentMenu && parentMenu.isConnected) {
                parentMenu.querySelectorAll(":scope > ul > .slide.has-sub > a").forEach((el) => {
                  try {
                    // Check if element still exists
                    if (!el || !el.isConnected) return;

                    const nextSib = el.nextElementSibling;
                    if (el !== element && nextSib && nextSib.nodeType === Node.ELEMENT_NODE && nextSib.tagName === 'UL' && nextSib.isConnected) {
                      try {
                        const siblingStyle = window.getComputedStyle(nextSib);
                        if (siblingStyle.display === "block" || (nextSib.style && nextSib.style.display === "block")) {
                          slideUp(nextSib);
                        }
                      } catch (err) {
                        // Skip if element is not accessible
                        console.warn('Error accessing element style:', err);
                      }
                    }
                  } catch (err) {
                    // Skip this element if there's an error
                    console.warn('Error processing menu item:', err);
                  }
                });
              }
              slideToggle(nextSibling);
            }
            // If no submenu, allow normal link navigation (don't prevent default)
          } catch (err) {
            // Catch any unexpected errors to prevent crashes
            console.warn('Error in sidebar click handler:', err);
          }
        };

        element.addEventListener("click", clickHandler);
        clickHandlers.push({ element, handler: clickHandler });
      });

      // Inner level menu items
      const innerLevelItems = document.querySelectorAll(".nav > ul > .slide.has-sub .slide.has-sub > a");
      innerLevelItems.forEach((element) => {
        const clickHandler = (e) => {
          try {
            // Check if element still exists and is connected to DOM
            if (!element || !element.isConnected) return;

            const nextSibling = element.nextElementSibling;

            // Only prevent default and toggle if there's a submenu (UL element)
            if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'UL') {
              e.preventDefault();

              const innerMenu = element.closest(".sidebar-menu");
              if (innerMenu && innerMenu.isConnected) {
                innerMenu.querySelectorAll(":scope .slide.has-sub > a").forEach((el) => {
                  try {
                    // Check if element still exists
                    if (!el || !el.isConnected) return;

                    const nextSib = el.nextElementSibling;
                    if (el !== element && nextSib && nextSib.nodeType === Node.ELEMENT_NODE && nextSib.tagName === 'UL' && nextSib.isConnected) {
                      try {
                        const siblingStyle = window.getComputedStyle(nextSib);
                        if (siblingStyle.display === "block" || (nextSib.style && nextSib.style.display === "block")) {
                          slideUp(nextSib);
                        }
                      } catch (err) {
                        // Skip if element is not accessible
                        console.warn('Error accessing element style:', err);
                      }
                    }
                  } catch (err) {
                    // Skip this element if there's an error
                    console.warn('Error processing menu item:', err);
                  }
                });
              }
              slideToggle(nextSibling);
            }
            // If no submenu, allow normal link navigation (don't prevent default)
          } catch (err) {
            // Catch any unexpected errors to prevent crashes
            console.warn('Error in sidebar click handler:', err);
          }
        };

        element.addEventListener("click", clickHandler);
        clickHandlers.push({ element, handler: clickHandler });
      });

      // Return cleanup function
      return () => {
        clickHandlers.forEach(({ element, handler }) => {
          if (element && element.isConnected) {
            element.removeEventListener("click", handler);
          }
        });
      };
    };

    // Small delay to ensure DOM is ready
    let cleanupFunction = null;
    const timer = setTimeout(() => {
      cleanupFunction = initializeSidebar();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (cleanupFunction) {
        cleanupFunction();
      }
    };
  }, []);

  return (
    <>
      { /* Start app-sidebar */}
      <aside className="app-sidebar sticky" id="sidebar">

        { /* start app-sidebar-header */}
        <div className="app-sidebar-header">
          <a href="/" className="desktop-logo">
            <img width='75' src="/assets/images/burray/burray.png" alt="image" />
          </a>
        </div>
        { /* end app-sidebar-header */}

        { /* start app-sidebar-wrapper */}

        <div className="app-sidebar-wrapper" id="sidebar-scroll">
          <nav className="app-sidebar-menu-wrapper nav flex-column sub-open">
            <div className="sidebar-left" id="sidebar-left" />
            <ul className="app-sidebar-main-menu">

              <li className="sidebar-menu-category"><span className="category-name">Main</span></li>
              <li className="slide has-sub">
                <Link to="/admin/dashboard" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiDashboardLine />
                  </div>
                  <span className="sidebar-menu-label">
                    Dashboard
                  </span>
                </Link>

              </li>

              <li className="slide has-sub">
                <Link to="/admin/all-companies" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiBuildingLine />
                  </div>
                  <span className="sidebar-menu-label">Companies</span>
                </Link>

              </li>

              <li className="slide has-sub">
                <Link to="/admin/hr-managers" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiUserLine />
                  </div>
                  <span className="sidebar-menu-label">Admins</span>
                </Link>
              </li>

              <li className="slide has-sub">
                <Link to="/admin/modules" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiPagesLine />
                  </div>
                  <span className="sidebar-menu-label">Modules</span>
                </Link>
              </li>

              <li className="slide has-sub">
                <Link to="/admin/packages" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiCalendarLine />
                  </div>
                  <span className="sidebar-menu-label">Packages</span>
                </Link>

              </li>


              <li className="slide has-sub">
                <Link to="/admin/holidays" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiCalendarLine />
                  </div>
                  <span className="sidebar-menu-label">Holidays</span>
                </Link>
              </li>

              <li className="slide has-sub">
                <Link to="/admin/formulas" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiGitBranchLine />
                  </div>
                  <span className="sidebar-menu-label">Formulas</span>
                </Link>
              </li>
              <hr />
              <li className="slide has-sub">
                <Link to="/admin/website-analytics" className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiBarChartLine />
                  </div>
                  <span className="sidebar-menu-label"><b><i>Website Analytics</i></b></span>
                </Link>
              </li>
              <hr />
              <li className="slide has-sub">
                <Link to="/" onClick={handleLogout} className="sidebar-menu-item">
                  <div className="side-menu-icon">
                    <RiLogoutCircleLine />
                  </div>
                  <span className="sidebar-menu-label">Logout</span>
                </Link>
              </li>
            </ul>
            <div className="sidebar-right" id="sidebar-right" />
          </nav>
        </div>
        { /* end app-sidebar-wrapper */}
      </aside>
      <div className="app-offcanvas-overlay" />
    </>
  )
}

export default Sidebar